import type { HttpResponse, ResponseBody } from './types';

/** `application/json` and the `+json` suffix family, parameters ignored. */
function isJson(contentType: string | null): boolean {
  if (contentType === null) return false;
  const essence = contentType.split(';')[0]?.trim().toLowerCase() ?? '';
  return essence === 'application/json' || essence.endsWith('+json');
}

/**
 * The default reading: JSON when the response says so, text otherwise.
 *
 * The content-type decides and the bytes never do. Sniffing them would make the
 * answer depend on what the first character happens to be, so a text body that
 * opens with a brace would come back as an object, and nothing in the result
 * type would tell the caller it happened. No content-type therefore means text:
 * that is the shape every body has, not a guess about what it might contain.
 *
 * A caller who knows better passes a `deserializer`, which replaces this
 * entirely and carries its own return type. That is the ONE override, and it is
 * deliberately the only one: an option that merely names a shape would decide
 * the same question a second time without adding anything the deserializer
 * cannot already say, and it would not type the result either.
 */
export function decodeBody(
  body: ResponseBody,
  response: HttpResponse,
): Promise<unknown> {
  return isJson(response.headers.get('content-type'))
    ? body.json()
    : body.text();
}
